import React, { useEffect, useState } from "react";
import { Repeat, RotateCw, ArrowRightCircle } from "lucide-react";
import { toast } from "sonner";

import { tasksApi, recurrenceApi } from "@/lib/api";
import { fmtTime, fmtDate } from "@/lib/format";
import RecurrencePicker, { presetFromRule } from "./RecurrencePicker";

/**
 * Recurrence controls for a parent task in TaskDetailDrawer.
 *
 *   - Shows the current rule (or "NONE") and a preset picker.
 *   - "MATERIALISE" spawns the remaining occurrences (idempotent).
 *   - "ROLL UNDONE → NEXT" copies planned/in-progress subtasks of the CURRENT
 *     task to the next chronological sibling in the family. Source subtasks
 *     get status="spilled" so producer sees provenance.
 *
 * Hidden for subtasks (recurrence is a master-level concept).
 */
export default function RecurrenceSection({ task, onMutated }) {
  const [busy, setBusy] = useState(false);
  const [pendingRule, setPendingRule] = useState(task?.recurrence_rule || null);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!task?.id) return;
    if (task.recurrence_parent_id || task.is_recurring) {
      recurrenceApi.info(task.id)
        .then(setInfo)
        .catch(() => setInfo(null));
    } else {
      setInfo(null);
    }
  }, [task?.id, task?.recurrence_parent_id, task?.is_recurring]);

  if (!task || task.parent_task_id) return null;

  const isMaster = !!task.recurrence_parent_id ? false : !!(task.is_recurring || task.recurrence_rule);
  const isChild = !!task.recurrence_parent_id;
  const currentRule = task.recurrence_rule || null;
  const { key, count } = presetFromRule(currentRule);

  const saveRule = async (nextRule) => {
    if (nextRule === currentRule) return;
    setBusy(true);
    try {
      await tasksApi.update(task.id, {
        is_recurring: !!nextRule,
        recurrence_rule: nextRule,
      });
      onMutated && onMutated();
      toast.success(nextRule ? "Recurrence saved" : "Recurrence cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save recurrence");
    } finally {
      setBusy(false);
    }
  };

  const materialize = async () => {
    setBusy(true);
    try {
      const r = await tasksApi.materialize(task.id);
      if (r.created_count === 0) {
        toast.info("Already materialised — no new occurrences.");
      } else {
        toast.success(`Materialised ${r.created_count} occurrence${r.created_count === 1 ? "" : "s"}`);
      }
      onMutated && onMutated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Materialise failed");
    } finally {
      setBusy(false);
    }
  };

  const rollover = async () => {
    setBusy(true);
    try {
      const r = await tasksApi.rollover(task.id);
      if (r.rolled === 0) {
        toast.info("Nothing to roll — no undone subtasks.");
      } else {
        toast.success(`Rolled ${r.rolled} subtask${r.rolled === 1 ? "" : "s"} to next occurrence`);
      }
      onMutated && onMutated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rollover failed");
    } finally {
      setBusy(false);
    }
  };

  if (isChild) {
    // For materialised children, surface the rollover affordance only.
    return (
      <div className="border-l-2 border-amber pl-3 py-2 space-y-2" data-testid="recurrence-section-child">
        <div className="mono text-2xs uppercase tracking-[0.2em] text-amber flex items-center gap-1.5">
          <Repeat className="w-3 h-3" /> RECURRING INSTANCE
        </div>
        {info?.is_recurring_family && info.ordinal && (
          <div className="mono text-2xs uppercase tracking-widest text-ink-dim" data-testid="recurrence-instance-info">
            Instance {info.ordinal} of {info.total}
            {info.next_start_at && (
              <span className="text-cyan ml-2">
                · Next: {fmtDate(info.next_start_at)} {fmtTime(info.next_start_at)}
              </span>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={rollover}
          disabled={busy}
          className="btn-ghost"
          data-testid="recurrence-rollover-btn"
          title="Move planned/in-progress subtasks to the next occurrence"
        >
          <ArrowRightCircle className="w-3 h-3" /> ROLL UNDONE → NEXT
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="recurrence-section-master">
      <RecurrencePicker
        value={pendingRule ?? currentRule}
        onChange={(r) => {
          setPendingRule(r);
          saveRule(r);
        }}
        disabled={busy}
      />
      {info?.is_recurring_family && info.ordinal && (
        <div className="mono text-2xs uppercase tracking-widest text-ink-dim border-l border-amber/30 pl-2" data-testid="recurrence-instance-info">
          Instance {info.ordinal} of {info.total}
          {info.next_start_at && (
            <span className="text-cyan ml-2">
              · Next: {fmtDate(info.next_start_at)} {fmtTime(info.next_start_at)}
            </span>
          )}
        </div>
      )}
      {isMaster && key !== "none" && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={materialize}
            disabled={busy}
            className="btn-primary"
            data-testid="recurrence-materialize-btn"
            title={`Spawn the remaining ${Math.max(0, count - 1)} occurrence(s)`}
          >
            <RotateCw className="w-3 h-3" /> MATERIALISE {Math.max(0, count - 1)}×
          </button>
          <button
            type="button"
            onClick={rollover}
            disabled={busy}
            className="btn-ghost"
            data-testid="recurrence-rollover-btn"
            title="Move undone subtasks of THIS task to the next occurrence"
          >
            <ArrowRightCircle className="w-3 h-3" /> ROLL UNDONE → NEXT
          </button>
        </div>
      )}
    </div>
  );
}
